// Resolves which protocol a node belongs to — from the API's own
// `protocol` field, and ONLY that field. An earlier version of this file
// also guessed from the node's `role` string (treating role names like
// "router" or "observer" as protocol-exclusive) and, failing that, from
// packet traffic mentioning the node's id. Both were real bugs, found
// verifying against the live backend (2026-09-20):
//
//   - The live backend's real Meshtastic roles are "CLIENT_BASE",
//     "CLIENT_MUTE", "ROUTER", "ROUTER_LATE", "TRACKER" — uppercase, and
//     "CLIENT_BASE" isn't even in Meshtastic's own published role enum, so
//     a hand-maintained alias table was never going to reliably cover real
//     data. Guessing protocol from role text is exactly the "Meshtastic-
//     as-base thinking" the protocol-split work was meant to eliminate —
//     it just moved the assumption from the taxonomy into the inference
//     chain instead of removing it.
//   - Worse, it produced *inconsistent* results: nodes with a role the
//     table recognised resolved correctly; nodes with a role it didn't
//     (or no role at all — 76 of 206 on the live backend) fell through to
//     "unknown", which was then exempted from the protocol-toggle's dim
//     behaviour entirely. The result looked like "the toggle does nothing
//     for most nodes" — because for most nodes, it didn't, and nothing
//     said why.
//
// The fix: protocol comes from the field, full stop. If the backend hasn't
// populated it yet (true for every node on the live backend today — see
// the build report), the node is honestly "unknown", not guessed into
// either protocol's colour/shape language. See LiveMap.tsx for how
// "unknown" is now rendered as its own distinct, filter-responsive,
// legend-documented state rather than a silent default.
import type { MeshNode } from "../api/types";
import type { Protocol } from './definitions/protocols';

export type ResolvedProtocol = Protocol | "unknown";

export function resolveNodeProtocol(node: MeshNode): ResolvedProtocol {
  const declared = node.protocol;
  if (declared === "meshtastic" || declared === "meshcore") return declared;
  return "unknown";
}
